import { useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/shadcn/card";
import { Button } from "@/components/ui/shadcn/button";
import { RefreshCw, AlertCircle, Wifi, WifiOff, ArrowRight, ArrowDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PublicQuote, QuoteWSUpdate, QuoteAcceptRequest, QuoteSide, QuoteUnit } from "@/types/quotes";
import { publicQuotesAPI } from "@/services/api";
import { formatAssetAmount } from "@/lib/utils";

interface ActiveQuoteProps {
  quote: PublicQuote;
  quotePrices: QuoteWSUpdate | null;
  timeLeft: number | null;
  timeRemaining: string;
  resetCounter: number;
  error: string | null;
  onAccept: () => void;
  onAcceptError: (error: string) => void;
  slug: string;
  isSocketConnected: boolean;
  isRefreshing: boolean;
}

export function ActiveQuoteComponent({
  quote,
  quotePrices,
  timeLeft,
  timeRemaining,
  resetCounter,
  error,
  onAccept,
  onAcceptError,
  slug,
  isSocketConnected,
  isRefreshing,
}: ActiveQuoteProps) {
  const [accepting, setAccepting] = useState(false);

  /** Validate input before accepting quote */
  const validateQuoteAcceptance = (): boolean => {
    if (!quote || !quotePrices) {
      onAcceptError("Información de cotización faltante");
      return false;
    }
    return true;
  };

  /** Handle accepting the quote */
  const handleAcceptQuote = async () => {
    if (!validateQuoteAcceptance()) return;

    setAccepting(true);

    try {
      const acceptData: QuoteAcceptRequest = {
        price: quotePrices!.price,
        total_from: quotePrices!.total_from,
        total_to: quotePrices!.total_to,
      };

      await publicQuotesAPI.acceptQuote(slug, acceptData);
      onAccept();
    } catch (error) {
      onAcceptError(
        "No se pudo aceptar la cotización. La cotización puede haber expirado o el precio cambió."
      );
    } finally {
      setAccepting(false);
    }
  };

  const isBuy = quote.side === QuoteSide.BUY;

  // For BUY:
  //   - total_from is always in counter (MXN)
  //   - total_to is always in base (BTC)
  // For SELL:
  //   - total_from is always in base (BTC)
  //   - total_to is always in counter (MXN)
  const fromAmount = isBuy
    ? quotePrices?.total_from // BUY: Give counter (MXN)
    : quotePrices?.total_from; // SELL: Give base (BTC)

  const toAmount = isBuy
    ? quotePrices?.total_to // BUY: Get base (BTC)
    : quotePrices?.total_to; // SELL: Get counter (MXN)

  // For BUY: fromAsset is counter (MXN), toAsset is base (BTC)
  // For SELL: fromAsset is base (BTC), toAsset is counter (MXN)
  const fromAsset = isBuy ? quote.counter : quote.base;
  const toAsset = isBuy ? quote.base : quote.counter;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0B0F1A] p-6">
      {/* BAX OTC Logo */}
      <div className="flex items-center mb-2">
        <img src="/logo_bax.png" alt="BAX Logo" className="h-8 md:h-10 mr-3" />
        <span className="font-[165] text-gray-400 tracking-[0.3em] text-[2.2rem] md:text-[2.8rem] mt-[0.2rem]">
          OTC
        </span>
      </div>

      {/* Main Card */}
      <Card className="w-full max-w-lg bg-[#111827]/90 backdrop-blur-xl shadow-2xl rounded-2xl md:rounded-3xl overflow-hidden">
        <CardHeader className="px-4 md:px-8 pt-6 md:pt-8 pb-0">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg md:text-2xl font-medium text-white">
              Cotización OTC
            </CardTitle>
            <AnimatePresence mode="wait">
              <motion.div
                key={isSocketConnected ? "connected" : "disconnected"}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                {isSocketConnected ? (
                  <div className="flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#1C3238] rounded-full">
                    <Wifi className="h-3 w-3 md:h-4 md:w-4 text-[#26C6DA]" />
                    <span className="text-[#26C6DA] text-xs md:text-sm font-medium">Conectado</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-red-900/20 rounded-full">
                    <WifiOff className="h-3 w-3 md:h-4 md:w-4 text-red-400" />
                    <span className="text-red-400 text-xs md:text-sm font-medium">Sin conexión</span>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </CardHeader>

        <CardContent className="px-4 md:px-8 pt-4 md:pt-6">
          <div className="space-y-4">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center space-x-2 bg-red-500/10 border border-red-400/30 rounded-xl p-3 md:p-4"
                >
                  <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-red-300" />
                  <p className="text-red-200 text-xs md:text-sm font-medium">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Exchange Display */}
            <div className="flex flex-col space-y-4">
              {/* Exchange Box Container */}
              <div className="bg-[#0D1526] rounded-xl md:rounded-3xl px-3 md:px-6 py-3 md:py-4 space-y-1 border border-gray-800">
                {/* Entregarás Section */}
                <div className="space-y-1.5">
                  <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                    Entregarás:
                  </span>

                  {/* From Asset Box */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50"
                  >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                      <div className="flex items-center space-x-2 md:space-x-3">
                        <div className="relative">
                          {fromAsset?.image_url && (
                            <img
                              src={fromAsset.image_url}
                              alt={fromAsset.code}
                              className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                            />
                          )}
                        </div>
                        <span className="text-base md:text-xl font-medium text-white">{fromAsset?.code}</span>
                      </div>
                      <div className="text-lg md:text-2xl font-medium text-[#FF6B00] text-center">
                        {formatAssetAmount(
                          fromAmount?.toString(),
                          fromAsset!
                        )}
                        {" "}
                        {fromAsset?.code}
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Arrow Divider */}
                <div className="flex items-center justify-center pt-2 md:pt-3">
                  <div className="h-[1px] flex-1 bg-gray-800"></div>
                  <div className="bg-[#080B12] p-1.5 md:p-2 rounded-full border border-gray-800/50">
                    <ArrowDown className="h-3 w-3 md:h-4 md:w-4 text-gray-500" />
                  </div>
                  <div className="h-[1px] flex-1 bg-gray-800"></div>
                </div>

                {/* Recibirás Section */}
                <div className="space-y-1.5 pb-1">
                  <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                    Recibirás:
                  </span>

                  {/* To Asset Box */}
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50"
                  >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-0">
                      <div className="flex items-center space-x-2 md:space-x-3">
                        <div className="relative">
                          {toAsset?.image_url && (
                            <img
                              src={toAsset.image_url}
                              alt={toAsset.code}
                              className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                            />
                          )}
                        </div>
                        <span className="text-base md:text-xl font-medium text-white">{toAsset?.code}</span>
                      </div>
                      <div className="text-lg md:text-2xl font-medium text-[#00E676] text-center">
                        {formatAssetAmount(
                          toAmount?.toString(),
                          toAsset!
                        )}
                        {" "}
                        {toAsset?.code}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              <div className="flex flex-col gap-0">
                {/* Price Section */}
                <div className="space-y-1.5">
                  <span className="text-xs md:text-sm font-medium text-gray-400 px-1">
                    Precio:
                  </span>

                  {/* Price Box */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full bg-[#080B12] rounded-lg md:rounded-2xl p-3 md:p-5 hover:bg-[#0B1221] transition-colors border border-gray-800/50"
                  >
                    <div className="flex justify-center items-center">
                      {isRefreshing && (
                        <RefreshCw className="h-4 w-4 md:h-5 md:w-5 animate-spin text-[#26C6DA] mr-2 md:mr-3" />
                      )}
                      <span className={`text-base md:text-xl font-semibold tracking-wide ${isRefreshing ? 'text-gray-400' : 'text-[#FFD700]'}`}>
                        1 {quote.base.code} = {formatAssetAmount(
                          quotePrices?.price,
                          quote.counter,
                        )}
                        {" "}
                        {quote.counter.code}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Countdown Timer */}
                <div className="w-full space-y-2 pt-3 md:pt-4 px-1">
                  {timeLeft !== null && (
                    <div className="flex justify-center">
                      <div className="w-full relative h-1 rounded-full bg-[#0B1221] overflow-hidden">
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-[#26C6DA] to-[#0091EA] rounded-full"
                          key={resetCounter}
                          initial={{ width: "100%" }}
                          animate={{ width: "0%" }}
                          transition={{ duration: timeLeft, ease: "linear" }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 text-xs md:text-sm text-gray-400">
                    <RefreshCw className={`${isRefreshing ? "h-3 w-3 md:h-4 md:w-4 animate-spin text-[#26C6DA]" : "h-[0.8rem] w-[0.8rem] md:h-[0.9rem] md:w-[0.9rem]"}`} />
                    <span>
                      {isRefreshing
                        ? "Actualizando precio..."
                        : timeLeft !== null
                        ? <>
                          El precio se actualizará en&nbsp;&nbsp;
                          <span className="text-[#FFD700] font-semibold">{timeLeft.toFixed(0)}s</span>
                        </>
                        : "Esperando actualización..."}
                    </span>
                  </div>
                </div>
              </div>

              {/* Accept Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full pt-3 md:pt-4"
              >
                <Button
                  className={`
                    w-full py-4 md:py-6 text-sm md:text-lg font-medium rounded-xl
                    ${accepting || isRefreshing || !isSocketConnected
                      ? "bg-gray-700/50 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#26C6DA] to-[#0091EA] hover:from-[#26C6DA]/90 hover:to-[#0091EA]/90 text-white"
                    }
                    transition-all duration-200 flex items-center justify-center gap-2
                  `}
                  onClick={handleAcceptQuote}
                  disabled={accepting || isRefreshing || !isSocketConnected}
                >
                  {accepting ? (
                    <>
                      <RefreshCw className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      Aceptar Cotización
                      <ArrowRight className="h-4 w-4 md:h-5 md:w-5" />
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </CardContent>

        <CardFooter className="justify-center px-4 md:px-8 pb-4 pt-2 md:pt-3">
          <div className="flex text-gray-400 text-xs md:text-sm">
            Este enlace dejará de ser válido en {" "}
            <span className="text-white ml-1">{timeRemaining}</span>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
